<script lang="ts">
  import { api, OfflineError } from '../lib/api'
  import { config } from '../lib/config'
  import { ripple } from '../lib/ripple'
  import type { EmailPreferences } from '../lib/types'
  import Notice from '../lib/components/Notice.svelte'
  import Loading from '../lib/components/Loading.svelte'

  /*
   * Where an unsubscribe link lands. The token in `?t=` is the credential, so there is no session and no
   * sign-in bounce — somebody arriving from an email must be able to stop the email.
   *
   * What the token is worth is the API's to decide, and this page does not second-guess it: `fullCentre`
   * and `mayGrant` are read, never recomputed.
   */

  // The API knows category names; the words belong here.
  const CATEGORY_LABELS: Record<string, string> = {
    'support-site-idea-updates': 'Idea updates from the support site',
  }

  const token = new URLSearchParams(location.search).get('t') ?? ''

  let preferences = $state<EmailPreferences | null>(null)
  let failed = $state(false)
  let busy = $state<string | null>(null)
  let outcome = $state<string | null>(null)
  let error = $state<string | null>(null)

  async function load() {
    if (!token) {
      failed = true
      return
    }
    try {
      preferences = await api.emailPreferences(token)
    } catch {
      failed = true
    }
  }

  async function setCategory(input: HTMLInputElement, name: string, optedOut: boolean) {
    if (busy) return
    busy = name
    outcome = null
    error = null
    try {
      await api.changeEmailPreference(token, { category: name, optedOut })
      preferences = await api.emailPreferences(token)
      outcome = optedOut
        ? `You will no longer receive ${(CATEGORY_LABELS[name] ?? name).toLowerCase()}.`
        : `You will receive ${(CATEGORY_LABELS[name] ?? name).toLowerCase()} again.`
    } catch (e) {
      error = message(e)
      // The browser has already moved the box and nothing else puts it back, so a failed save would leave
      // it showing a position the account does not hold. `optedOut` is what was ATTEMPTED, so the box was
      // showing its opposite before the click, which is what it must return to.
      input.checked = optedOut
    } finally {
      busy = null
    }
  }

  async function withdrawMarketing() {
    if (busy) return
    busy = 'marketing'
    outcome = null
    error = null
    try {
      await api.changeEmailPreference(token, { withdrawMarketing: true })
      preferences = await api.emailPreferences(token)
      outcome = 'You will no longer receive Bird Journal news and offers.'
    } catch (e) {
      error = message(e)
    } finally {
      busy = null
    }
  }

  const message = (e: unknown) =>
    e instanceof OfflineError
      ? 'Could not reach Bird Journal. Check your connection and try again.'
      : 'We could not save that just now. Please try again.'

  const accountUrl = $derived(`${config().marketing}/account/email`)

  load()
</script>

{#if failed}
  <Notice tone="error">
    This link is no longer valid. Sign in to your Bird Journal account to change which emails you receive.
  </Notice>
  <p><a href={accountUrl}>Go to your account</a></p>
{:else if preferences === null}
  <Loading rows={4} />
{:else}
  <p>Choose which emails you'd like to receive from Bird Journal.</p>

  {#if error}
    <Notice tone="error" live>{error}</Notice>
  {:else if outcome}
    <Notice tone="ok" live>{outcome}</Notice>
  {/if}

  {#if preferences.categories.length > 0}
    <ul class="categories">
      {#each preferences.categories as category (category.name)}
        <li>
          <label class="choice">
            <input
              type="checkbox"
              checked={!category.optedOut}
              disabled={busy !== null}
              onchange={(event) =>
                setCategory(event.currentTarget, category.name, !event.currentTarget.checked)}
            />
            <span>{CATEGORY_LABELS[category.name] ?? category.name}</span>
          </label>
        </li>
      {/each}
    </ul>
  {/if}

  <!-- A status line with one action, never a checkbox beside the categories: unticked there means "you
       have not objected", unticked here would mean "you never said yes". -->
  <section class="marketing">
    <h2>News &amp; offers</h2>
    {#if preferences.marketing.optedIn}
      <p>You currently receive occasional Bird Journal news and offers by email.</p>
      <button
        class="btn btn-outline btn-sm rip"
        use:ripple
        type="button"
        disabled={busy !== null}
        onclick={withdrawMarketing}
      >
        {busy === 'marketing' ? 'Saving…' : 'Stop sending these'}
      </button>
    {:else}
      <p>You do not receive Bird Journal news and offers.</p>
    {/if}
  </section>

  {#if !preferences.fullCentre}
    <p class="aside">
      For your privacy, a link this old shows only the emails the message you followed was about.
      <a href={accountUrl}>Sign in to your account</a> to see all your email settings.
    </p>
  {:else if !preferences.marketing.optedIn}
    <p class="aside">
      <a href={accountUrl}>Sign in to your account</a> if you'd like to start receiving news and offers.
    </p>
  {/if}

  <p class="aside">
    This does not affect emails we have to send you about your account — receipts, security alerts and
    password resets.
  </p>
{/if}

<style>
  .categories {
    list-style: none;
    margin: 0 0 var(--space-8);
    padding: 0;
  }
  .marketing {
    border-top: 1px solid var(--bj-outline-variant);
    padding-top: var(--space-6);
  }
  .marketing h2 {
    font-size: 1rem;
    margin: 0 0 var(--space-2);
  }
  .aside {
    margin-top: var(--space-6);
    color: var(--bj-on-surface-variant);
    font-size: 0.9rem;
  }
</style>
